import { useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Moon, Sun, Menu, X } from 'lucide-react'
import { useTheme } from '../../../shared/hooks/useTheme'
import { cn } from '@/lib/utils'
import { LanguageToggle } from './LanguageToggle'
import { AnimatedLogo } from './AnimatedLogo'

export function LandingHeader() {
  const { t } = useTranslation()
  const { theme, toggleTheme } = useTheme()
  const [mobileOpen, setMobileOpen] = useState(false)

  const links = [
    { to: '/features', label: t('landing.header.nav.features') },
    { to: '/platform', label: t('landing.header.nav.platform') },
    { to: '/use-cases', label: t('landing.header.nav.useCases') },
    { to: '/pricing', label: t('landing.header.nav.pricing') },
  ]

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50',
        'border-b border-border bg-background/80 shadow-sm backdrop-blur-xl',
      )}
    >
      <div className="mx-auto flex h-14 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link
          to="/"
          className="inline-flex items-center gap-2.5 text-foreground no-underline"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-secondary ring-1 ring-border">
            <AnimatedLogo size={18} />
          </span>
          <span className="text-sm font-medium tracking-wide">Omni Sight</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 md:flex">
          {links.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'text-[13px] no-underline transition-colors duration-150',
                  isActive
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Right controls */}
        <div className="hidden items-center gap-2 md:flex">
          <LanguageToggle />

          <button
            type="button"
            onClick={toggleTheme}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors duration-150 hover:bg-secondary hover:text-foreground"
            aria-label={t('topbar.theme')}
          >
            {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
          </button>

          <Link to="/login">
            <span className="inline-flex h-8 items-center justify-center rounded-lg px-3.5 text-[13px] font-medium text-muted-foreground transition-colors duration-150 hover:bg-secondary hover:text-foreground">
              {t('landing.header.login')}
            </span>
          </Link>

          <Link to="/pricing#contact">
            <span className="inline-flex h-8 items-center justify-center rounded-lg bg-cta px-4 text-[13px] font-medium text-cta-foreground transition-all duration-150 hover:bg-cta-hover">
              {t('landing.header.contactSales')}
            </span>
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary md:hidden"
          aria-label="Menu"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div
          className={cn(
            'border-t border-border px-4 pb-4 pt-3 md:hidden',
            'bg-background/95 backdrop-blur-xl',
          )}
        >
          <nav className="flex flex-col gap-1">
            {links.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'rounded-lg px-3 py-2 text-[13px] no-underline transition-colors duration-150',
                    isActive
                      ? 'text-foreground bg-secondary/50'
                      : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-3 flex items-center gap-2 border-t border-border/50 pt-3">
            <LanguageToggle />
            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary"
              aria-label={t('topbar.theme')}
            >
              {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
            </button>
            <div className="ml-auto flex items-center gap-2">
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="inline-flex h-8 items-center rounded-lg px-3 text-[13px] font-medium text-muted-foreground no-underline hover:bg-secondary"
              >
                {t('landing.header.login')}
              </Link>
              <Link
                to="/pricing#contact"
                onClick={() => setMobileOpen(false)}
                className="inline-flex h-8 items-center rounded-lg bg-cta px-4 text-[13px] font-medium text-cta-foreground no-underline"
              >
                {t('landing.header.contactSales')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
